import React, { useEffect, useRef, useState } from 'react';
import { useRelay } from '../../../hooks/useRelay';
import {
  applyAction, getBotAction, getPlayerView,
  ClassicCardsState, ClassicCardsAction,
  War, Holdem, Gin, Hearts, Spades,
} from '../../../games/classic-cards/classic-cards';
import { cardName, deadwoodValue, findBestMelds, cardSuit, cardRank } from '../../../games/classic-cards/cards';
import type { RoomView } from '../../../types/room';

interface Props {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: ClassicCardsState | null;
  onFinish: () => void;
}

export default function ClassicCardsRoomGame({ slug, room, myId, isLeader, initialState, onFinish }: Props) {
  const { state, startRelay, sendAction } = useRelay<ClassicCardsState>(slug);
  const relayStartedRef = useRef(false);

  useEffect(() => {
    if (!isLeader || !initialState || relayStartedRef.current) return;
    relayStartedRef.current = true;
    startRelay('classic-cards', initialState);
  }, [isLeader, initialState, startRelay]);

  // Bot actions: leader drives all bots after each state change
  useEffect(() => {
    if (!isLeader || !state) return;

    if (state.game === 'war') {
      const ws = state.data;
      if (ws.phase !== 'playing') return;
      const bots = ws.players.filter(p => p.isBot && p.action === null);
      if (bots.length === 0) return;

      let cur = ws;
      let lastAction: War.WarAction = { type: War.getBotAction(bots[0], cur), playerId: bots[0].id };
      cur = War.applyAction(cur, lastAction);
      for (let i = 1; i < bots.length; i++) {
        lastAction = { type: War.getBotAction(bots[i], cur), playerId: bots[i].id };
        cur = War.applyAction(cur, lastAction);
      }
      sendAction({ game: 'war', action: lastAction }, { game: 'war', data: cur });

    } else if (state.game === 'texas-holdem') {
      const hs = state.data;
      if (hs.phase === 'complete' || hs.phase === 'showdown') return;
      const toAct = hs.playersToAct[0];
      if (!toAct) return;
      const actor = hs.players.find(p => p.id === toAct);
      if (!actor?.isBot) return;
      const action = Holdem.getBotAction(toAct, hs);
      const newData = Holdem.applyAction(hs, action);
      sendAction({ game: 'texas-holdem', action }, { game: 'texas-holdem', data: newData });

    } else if (state.game === 'gin-rummy') {
      const gs = state.data;
      if (gs.phase === 'roundOver' || gs.phase === 'complete') return;
      const curPlayer = gs.players[gs.currentPlayerIndex];
      if (!curPlayer?.isBot) return;
      const action = Gin.getBotAction(curPlayer.id, gs);
      const newData = Gin.applyAction(gs, action);
      sendAction({ game: 'gin-rummy', action }, { game: 'gin-rummy', data: newData });

    } else if (state.game === 'hearts') {
      const hs = state.data;
      if (hs.phase === 'roundOver' || hs.phase === 'complete') return;

      if (hs.phase === 'passing') {
        // Drive the next bot that hasn't passed yet (one at a time)
        const botToPas = hs.players.find(p => p.isBot && p.passedCards === null);
        if (!botToPas) return;
        const action = Hearts.getBotAction(botToPas.id, hs);
        const newData = Hearts.applyAction(hs, action);
        sendAction({ game: 'hearts', action }, { game: 'hearts', data: newData });
        return;
      }

      if (hs.phase === 'playing') {
        const actorIdx = (hs.trickLeaderIndex + hs.currentTrick.length) % 4;
        const actor = hs.players[actorIdx];
        if (!actor?.isBot) return;
        const action = Hearts.getBotAction(actor.id, hs);
        const newData = Hearts.applyAction(hs, action);
        sendAction({ game: 'hearts', action }, { game: 'hearts', data: newData });
      }

    } else if (state.game === 'spades') {
      const ss = state.data;
      if (ss.phase === 'roundOver' || ss.phase === 'complete') return;

      if (ss.phase === 'bidding') {
        const bidder = ss.players[ss.biddingIndex];
        if (!bidder?.isBot) return;
        const action = Spades.getBotAction(bidder.id, ss);
        const newData = Spades.applyAction(ss, action);
        sendAction({ game: 'spades', action }, { game: 'spades', data: newData });
        return;
      }

      if (ss.phase === 'playing') {
        const actorIdx = (ss.trickLeaderIndex + ss.currentTrick.length) % 4;
        const actor = ss.players[actorIdx];
        if (!actor?.isBot) return;
        const action = Spades.getBotAction(actor.id, ss);
        const newData = Spades.applyAction(ss, action);
        sendAction({ game: 'spades', action }, { game: 'spades', data: newData });
      }
    }
  }, [state, isLeader, sendAction]);

  if (!state) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#888', fontFamily: 'sans-serif' }}>Loading game...</div>;
  }

  const isSpectator = room.participants.find(p => p.id === myId)?.type === 'spectator';

  const send = (action: ClassicCardsAction) => {
    const next = applyAction(state, action);
    sendAction(action, next);
  };

  if (state.game === 'war') {
    return <WarView state={state.data} myId={myId} isSpectator={isSpectator} isLeader={isLeader} send={a => send({ game: 'war', action: a })} onFinish={onFinish} />;
  }
  if (state.game === 'texas-holdem') {
    return <HoldemView state={state.data} myId={myId} isSpectator={isSpectator} isLeader={isLeader} send={a => send({ game: 'texas-holdem', action: a })} onFinish={onFinish} />;
  }
  if (state.game === 'gin-rummy') {
    return <GinView state={state.data} myId={myId} isSpectator={isSpectator} isLeader={isLeader} send={a => send({ game: 'gin-rummy', action: a })} onFinish={onFinish} />;
  }
  if (state.game === 'hearts') {
    return <HeartsView state={state.data} myId={myId} isSpectator={isSpectator} isLeader={isLeader} send={a => send({ game: 'hearts', action: a })} onFinish={onFinish} />;
  }
  if (state.game === 'spades') {
    return <SpadesView state={state.data} myId={myId} isSpectator={isSpectator} isLeader={isLeader} send={a => send({ game: 'spades', action: a })} onFinish={onFinish} />;
  }
  return null;
}

// ===== WAR UI =====

function WarView({ state, myId, isSpectator, isLeader, send, onFinish }: {
  state: War.WarState; myId: string; isSpectator: boolean; isLeader: boolean;
  send: (a: War.WarAction) => void; onFinish: () => void;
}) {
  const viewPlayerId = isSpectator ? null : myId;
  const view = War.getPlayerView(state, viewPlayerId);
  const myPlayer = view.players.find(p => p.id === myId);
  const waiting = state.phase === 'playing' ? view.players.filter(p => p.action === null && !p.isBot).map(p => p.name) : [];
  const winnerName = view.players.find(p => p.id === state.winnerId)?.name;

  return (
    <div style={S.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Round {state.round}</h2>
        <span style={{ color: '#888', fontSize: '0.85em' }}>
          First to {state.config.winningScore}
          {state.config.variant === 'aces-high' ? ' · Aces High' : ''}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {view.players.map(p => (
          <div key={p.id} style={{ ...S.playerRow, background: p.id === myId ? '#f0f8ff' : '#fafafa', border: `1px solid ${p.id === myId ? '#b0d4f0' : '#ddd'}` }}>
            <span style={{ fontSize: '0.8em', color: '#888' }}>{p.isBot ? 'B' : 'H'}</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 500 }}>{p.name}</span>
              {p.id === myId && <span style={{ marginLeft: 6, fontSize: '0.8em', color: '#888' }}>(you)</span>}
            </div>
            <span style={{ color: '#888', fontSize: '0.85em', minWidth: 48, textAlign: 'right' }}>
              {p.score >= 0 ? `+${p.score}` : p.score} pts
            </span>
            <div style={{ minWidth: 72, textAlign: 'right' }}>
              <div style={{ fontWeight: 'bold' }}>{p.card !== null ? War.warCardName(p.card) : '—'}</div>
              <div style={{ fontSize: '0.75em', color: '#aaa' }}>
                {p.action === 'play' ? 'played' : p.action === 'fold' ? 'folded' : state.phase === 'playing' ? '...' : ''}
              </div>
            </div>
          </div>
        ))}
      </div>

      {state.roundResult && (
        <div style={S.resultBox}>{state.roundResult.message}</div>
      )}

      <div style={{ marginTop: 20, textAlign: 'center' }}>
        {state.phase === 'playing' && !isSpectator && myPlayer && myPlayer.action === null && !myPlayer.isBot && (
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => send({ type: 'play', playerId: myId })} style={S.btn}>Play</button>
            <button onClick={() => send({ type: 'fold', playerId: myId })} style={{ ...S.btn, background: '#fff' }}>Fold (−1)</button>
          </div>
        )}
        {waiting.length > 0 && <p style={{ color: '#888', marginTop: 8 }}>Waiting for {waiting.join(', ')}…</p>}
        {state.phase === 'revealing' && !state.winnerId && (
          <button onClick={() => send({ type: 'nextRound' })} style={S.btn}>Next Round</button>
        )}
        {state.phase === 'complete' && (
          <div>
            <div style={{ fontSize: '1.3em', fontWeight: 'bold', marginBottom: 16 }}>{winnerName ?? 'Someone'} wins!</div>
            {isLeader ? <button onClick={onFinish} style={S.btn}>Back to the room</button> : <div style={{ color: '#888' }}>Waiting for the host to go back to the room.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== TEXAS HOLD'EM UI =====

function HoldemView({ state, myId, isSpectator, isLeader, send, onFinish }: {
  state: Holdem.HoldemState; myId: string; isSpectator: boolean; isLeader: boolean;
  send: (a: Holdem.HoldemAction) => void; onFinish: () => void;
}) {
  const [raiseInput, setRaiseInput] = useState('');
  const view = Holdem.getPlayerView(state, isSpectator ? null : myId);
  const myPlayer = view.players.find(p => p.id === myId);
  const toAct = state.playersToAct[0];
  const isMyTurn = toAct === myId && !isSpectator;
  const valid = isMyTurn ? Holdem.getValidActions(state, myId) : [];
  const toCall = myPlayer ? state.currentBet - myPlayer.betInRound : 0;
  const winnerName = state.players.find(p => p.id === state.winnerId)?.name;

  const handleRaise = () => {
    const amount = parseInt(raiseInput, 10);
    if (!isNaN(amount)) {
      send({ type: 'raise', playerId: myId, amount });
      setRaiseInput('');
    }
  };

  return (
    <div style={S.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Hand #{state.handNumber}</h2>
        <span style={{ color: '#888', fontSize: '0.85em' }}>
          {state.phase}  ·  Pot: {state.pot}  ·  Bet: {state.currentBet}
        </span>
      </div>

      {/* Community cards */}
      <div style={{ ...S.resultBox, background: '#f5f5f5', marginBottom: 16 }}>
        <div style={{ fontSize: '0.8em', color: '#888', marginBottom: 4 }}>Community</div>
        <div style={{ fontSize: '1.3em', letterSpacing: 4 }}>
          {state.communityCards.length > 0 ? state.communityCards.map(cardName).join('  ') : '—'}
        </div>
      </div>

      {/* Players */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {view.players.map(p => {
          const isMe = p.id === myId;
          const acting = toAct === p.id;
          return (
            <div key={p.id} style={{ ...S.playerRow, background: isMe ? '#f0f8ff' : '#fafafa', border: `1px solid ${acting ? '#f0a040' : isMe ? '#b0d4f0' : '#ddd'}`, opacity: p.eliminated ? 0.4 : 1 }}>
              <span style={{ fontSize: '0.8em', color: '#888' }}>{p.isBot ? 'B' : 'H'}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 500 }}>{p.name}</span>
                {isMe && <span style={{ marginLeft: 6, fontSize: '0.8em', color: '#888' }}>(you)</span>}
                {acting && <span style={{ marginLeft: 6, fontSize: '0.8em', color: '#b06000' }}>← acting</span>}
                {p.allIn && <span style={{ marginLeft: 6, fontSize: '0.8em', color: '#e00' }}>ALL-IN</span>}
                {p.folded && !p.eliminated && <span style={{ marginLeft: 6, fontSize: '0.8em', color: '#888' }}>FOLDED</span>}
                {p.eliminated && <span style={{ marginLeft: 6, fontSize: '0.8em', color: '#888' }}>OUT</span>}
              </div>
              <div style={{ textAlign: 'right', fontSize: '0.85em' }}>
                <div style={{ color: '#888' }}>{p.chips} chips</div>
                {p.betInRound > 0 && <div style={{ color: '#666' }}>bet: {p.betInRound}</div>}
              </div>
              <div style={{ minWidth: 80, textAlign: 'right', fontWeight: 'bold' }}>
                {p.holeCards ? p.holeCards.map(cardName).join(' ') : (p.folded || p.eliminated ? '' : '? ?')}
              </div>
            </div>
          );
        })}
      </div>

      {/* Last hand result */}
      {(state.phase === 'showdown') && state.lastHandResult && (
        <div style={{ ...S.resultBox, marginTop: 16 }}>
          {state.lastHandResult.winners.map((w, i) => (
            <div key={i}>{w.playerName} wins {w.amount} chips — {w.description}</div>
          ))}
          {Object.entries(state.lastHandResult.allHoleCards).map(([id, cards]) => {
            const name = state.players.find(p => p.id === id)?.name ?? id;
            return <div key={id} style={{ fontSize: '0.85em', color: '#666' }}>{name}: {cards.map(cardName).join(' ')}</div>;
          })}
        </div>
      )}

      {/* Actions */}
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        {state.phase === 'showdown' && (
          <button onClick={() => send({ type: 'nextHand' })} style={S.btn}>Next Hand</button>
        )}
        {state.phase === 'complete' && (
          <div>
            <div style={{ fontSize: '1.3em', fontWeight: 'bold', marginBottom: 16 }}>{winnerName} wins!</div>
            {isLeader ? <button onClick={onFinish} style={S.btn}>Back to the room</button> : <div style={{ color: '#888' }}>Waiting for the host to go back to the room.</div>}
          </div>
        )}
        {isMyTurn && state.phase !== 'showdown' && state.phase !== 'complete' && (
          <div>
            <div style={{ marginBottom: 8, color: '#888', fontSize: '0.9em' }}>
              Your chips: {myPlayer?.chips}  ·  To call: {toCall}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {valid.includes('fold') && <button onClick={() => send({ type: 'fold', playerId: myId })} style={{ ...S.btn, background: '#fee' }}>Fold</button>}
              {valid.includes('check') && <button onClick={() => send({ type: 'check', playerId: myId })} style={S.btn}>Check</button>}
              {valid.includes('call') && <button onClick={() => send({ type: 'call', playerId: myId })} style={S.btn}>Call {toCall}</button>}
              {valid.includes('raise') && (
                <span style={{ display: 'flex', gap: 4 }}>
                  <input
                    type="number"
                    value={raiseInput}
                    placeholder={`Raise to (min ${state.currentBet + state.minRaise})`}
                    onChange={e => setRaiseInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleRaise()}
                    style={{ width: 140, padding: '6px 8px' }}
                  />
                  <button onClick={handleRaise} style={S.btn}>Raise</button>
                </span>
              )}
              {valid.includes('allin') && <button onClick={() => send({ type: 'allin', playerId: myId })} style={{ ...S.btn, background: '#ffe' }}>All-In ({myPlayer?.chips})</button>}
            </div>
          </div>
        )}
        {!isMyTurn && state.phase !== 'showdown' && state.phase !== 'complete' && toAct && (
          <p style={{ color: '#888' }}>Waiting for {state.players.find(p => p.id === toAct)?.name}…</p>
        )}
      </div>
    </div>
  );
}

// ===== GIN RUMMY UI =====

function GinView({ state, myId, isSpectator, isLeader, send, onFinish }: {
  state: Gin.GinState; myId: string; isSpectator: boolean; isLeader: boolean;
  send: (a: Gin.GinAction) => void; onFinish: () => void;
}) {
  const myPlayer = state.players.find(p => p.id === myId);
  const curPlayer = state.players[state.currentPlayerIndex];
  const isMyTurn = curPlayer?.id === myId && !isSpectator;
  const valid = isMyTurn ? Gin.getValidActions(state, myId) : [];
  const topDiscard = state.discardPile[state.discardPile.length - 1];
  const winnerName = state.players.find(p => p.id === state.winnerId)?.name;
  const opponent = state.players.find(p => p.id !== myId);

  // Compute meld info for my hand
  const myHand = myPlayer?.hand ?? [];
  const { melds, unmelded } = findBestMelds(myHand);
  const myDeadwood = unmelded.reduce((s, c) => s + deadwoodValue(c), 0);
  const meldSet = new Set(melds.flat());

  return (
    <div style={S.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Round {state.roundNumber}</h2>
        <span style={{ color: '#888', fontSize: '0.85em' }}>
          {state.players.map(p => `${p.name}: ${p.score}`).join('  ·  ')}
        </span>
      </div>

      {/* Piles */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        <div style={{ ...S.resultBox, flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '0.8em', color: '#888' }}>Stock</div>
          <div style={{ fontSize: '1.3em', fontWeight: 'bold' }}>{state.stockPile.length} cards</div>
          {isMyTurn && state.phase === 'drawing' && state.stockPile.length > 2 && (
            <button onClick={() => send({ type: 'draw', playerId: myId, source: 'stock' })} style={{ ...S.btn, marginTop: 8 }}>Draw Stock</button>
          )}
        </div>
        <div style={{ ...S.resultBox, flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '0.8em', color: '#888' }}>Discard</div>
          <div style={{ fontSize: '1.3em', fontWeight: 'bold' }}>{topDiscard !== undefined ? cardName(topDiscard) : '—'}</div>
          {isMyTurn && state.phase === 'drawing' && topDiscard !== undefined && (
            <button onClick={() => send({ type: 'draw', playerId: myId, source: 'discard' })} style={{ ...S.btn, marginTop: 8 }}>Take Discard</button>
          )}
        </div>
      </div>

      {/* Opponent info */}
      {opponent && (
        <div style={{ ...S.playerRow, marginBottom: 12 }}>
          <span style={{ fontSize: '0.8em', color: '#888' }}>{opponent.isBot ? 'B' : 'H'}</span>
          <span style={{ flex: 1, fontWeight: 500 }}>{opponent.name}</span>
          <span style={{ color: '#888', fontSize: '0.85em' }}>{opponent.hand.length} cards in hand</span>
          <span style={{ marginLeft: 12, color: '#888', fontSize: '0.85em' }}>Score: {opponent.score}</span>
        </div>
      )}

      {/* My hand */}
      {myPlayer && (
        <div>
          <div style={{ fontSize: '0.85em', color: '#888', marginBottom: 6 }}>
            Your hand ({myHand.length} cards) · Deadwood: {myDeadwood}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {myHand.map((card, i) => {
              const inMeld = meldSet.has(card);
              const canDiscard = isMyTurn && state.phase === 'discarding';
              return (
                <div key={i} style={{
                  padding: '8px 12px', border: `2px solid ${inMeld ? '#4a9' : '#ccc'}`,
                  borderRadius: 6, background: inMeld ? '#efffee' : '#fff',
                  cursor: canDiscard ? 'pointer' : 'default',
                  fontSize: '1.05em', fontWeight: inMeld ? 'bold' : 'normal',
                  userSelect: 'none',
                }}>
                  {canDiscard ? (
                    <div>
                      <div>{cardName(card)}</div>
                      <div style={{ fontSize: '0.65em', color: '#888', textAlign: 'center' }}>
                        {deadwoodValue(card)}pt
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
                        <button onClick={() => send({ type: 'discard', playerId: myId, cardIndex: i })} style={{ ...S.smallBtn }}>Discard</button>
                        {valid.includes('knock') && (() => {
                          const remaining = myHand.filter((_, j) => j !== i);
                          const { deadwood: dw } = findBestMelds(remaining);
                          return dw <= 10 ? (
                            <button onClick={() => send({ type: 'knock', playerId: myId, cardIndex: i })} style={{ ...S.smallBtn, background: '#ffe8a0' }}>Knock</button>
                          ) : null;
                        })()}
                        {valid.includes('gin') && (() => {
                          const remaining = myHand.filter((_, j) => j !== i);
                          const { deadwood: dw } = findBestMelds(remaining);
                          return dw === 0 ? (
                            <button onClick={() => send({ type: 'gin', playerId: myId, cardIndex: i })} style={{ ...S.smallBtn, background: '#a0e8a0' }}>Gin!</button>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  ) : (
                    cardName(card)
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 8, fontSize: '0.8em', color: '#888' }}>
            {melds.length > 0 && `Melds: ${melds.map(m => m.map(cardName).join('-')).join(', ')}`}
          </div>
        </div>
      )}

      {/* Status / result */}
      {!isMyTurn && state.phase !== 'roundOver' && state.phase !== 'complete' && (
        <p style={{ color: '#888', marginTop: 16 }}>Waiting for {curPlayer?.name}…</p>
      )}

      {state.roundResult && (
        <div style={{ ...S.resultBox, marginTop: 16 }}>
          {state.roundResult.type === 'gin' && <div>🃏 Gin! {state.roundResult.knockerName} goes gin.</div>}
          {state.roundResult.type === 'knock' && <div>Knock by {state.roundResult.knockerName} ({state.roundResult.knockerDeadwood} deadwood).</div>}
          {state.roundResult.type === 'undercut' && <div>Undercut! Opponent matched or beat the knock.</div>}
          {state.roundResult.type === 'draw' && <div>Draw — stock exhausted. No points.</div>}
          {state.roundResult.type !== 'draw' && (
            <div style={{ fontSize: '0.9em', color: '#666', marginTop: 4 }}>
              Knocker deadwood: {state.roundResult.knockerDeadwood} · Opponent: {state.roundResult.opponentDeadwood} · +{state.roundResult.points} pts
            </div>
          )}
          {state.phase === 'roundOver' && (
            <button onClick={() => send({ type: 'nextRound' })} style={{ ...S.btn, marginTop: 12 }}>Next Round</button>
          )}
        </div>
      )}

      {state.phase === 'complete' && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <div style={{ fontSize: '1.3em', fontWeight: 'bold', marginBottom: 16 }}>{winnerName} wins!</div>
          {isLeader ? <button onClick={onFinish} style={S.btn}>Back to the room</button> : <div style={{ color: '#888' }}>Waiting for the host to go back to the room.</div>}
        </div>
      )}
    </div>
  );
}

// ===== HEARTS UI =====

const SUIT_COLORS = ['#1a1a2e', '#c0392b', '#c0392b', '#1a1a2e']; // ♠♥♦♣
const SUIT_SYMBOLS = ['♠', '♥', '♦', '♣'];
const RANK_NAMES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

function HeartsCardLabel({ card }: { card: number }) {
  const rank = cardRank(card);
  const suit = cardSuit(card);
  return (
    <span style={{ color: SUIT_COLORS[suit] }}>
      {RANK_NAMES[rank]}{SUIT_SYMBOLS[suit]}
    </span>
  );
}

function HeartsView({ state, myId, isSpectator, isLeader, send, onFinish }: {
  state: Hearts.HeartsState; myId: string; isSpectator: boolean; isLeader: boolean;
  send: (a: Hearts.HeartsAction) => void; onFinish: () => void;
}) {
  const [selectedPass, setSelectedPass] = useState<number[]>([]);

  const view = Hearts.getPlayerView(state, isSpectator ? null : myId);
  const myPlayer = view.players.find(p => p.id === myId);
  const actorIdx = (state.trickLeaderIndex + state.currentTrick.length) % 4;
  const isMyTurn = view.players[actorIdx]?.id === myId && !isSpectator && state.phase === 'playing';
  const validToPlay = isMyTurn ? Hearts.validCardsForPlayer(state, myId) : [];
  const canPass = !isSpectator && state.phase === 'passing' && myPlayer?.passedCards === null;

  const passDirectionLabel = { left: '→ left', right: '← right', across: '↔ across', hold: '· hold (no pass)' }[state.passDirection];
  const winnerName = state.players.find(p => p.id === state.winnerId)?.name;

  const handleCardClick = (card: number) => {
    if (canPass) {
      setSelectedPass(prev =>
        prev.includes(card) ? prev.filter(c => c !== card) : prev.length < 3 ? [...prev, card] : prev
      );
    } else if (isMyTurn && validToPlay.includes(card)) {
      send({ type: 'play', playerId: myId, card });
    }
  };

  const handlePass = () => {
    if (selectedPass.length === 3) {
      send({ type: 'pass', playerId: myId, cards: selectedPass });
      setSelectedPass([]);
    }
  };

  // Group hand by suit for display
  const groupedHand: Record<number, number[]> = { 0: [], 1: [], 2: [], 3: [] };
  if (myPlayer) {
    for (const card of myPlayer.hand) groupedHand[cardSuit(card)].push(card);
  }

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Round {state.roundNumber}</h2>
        <span style={{ color: '#888', fontSize: '0.85em' }}>
          {state.heartsBroken ? '♥ broken' : '♥ not broken'}
          {' · '}{passDirectionLabel}
        </span>
      </div>

      {/* Scoreboard */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {view.players.map(p => {
          const actingNow = view.players[actorIdx]?.id === p.id && state.phase === 'playing';
          return (
            <div key={p.id} style={{
              padding: '6px 12px', borderRadius: 6, fontSize: '0.85em',
              background: p.id === myId ? '#e8f4ff' : '#f5f5f5',
              border: `1px solid ${actingNow ? '#f0a040' : p.id === myId ? '#90c4f0' : '#ddd'}`,
              fontWeight: p.id === myId ? 600 : 400,
            }}>
              <div>{p.name}{p.isBot ? ' 🤖' : ''}{p.id === myId ? ' (you)' : ''}</div>
              <div style={{ color: '#666' }}>{p.totalScore} pts {p.trickPoints > 0 ? `· +${p.trickPoints} this round` : ''}</div>
              {state.phase === 'passing' && (
                <div style={{ fontSize: '0.8em', color: p.passedCards !== null ? '#4a9' : '#e88' }}>
                  {p.passedCards !== null ? 'passed ✓' : 'pending…'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Current trick */}
      {(state.phase === 'playing' || state.currentTrick.length > 0) && (
        <div style={{ ...S.resultBox, marginBottom: 16 }}>
          <div style={{ fontSize: '0.8em', color: '#888', marginBottom: 6 }}>
            Trick {state.tricksPlayed + 1} of 13
          </div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            {view.players.map(p => {
              const played = state.currentTrick.find(tc => tc.playerId === p.id);
              const isLeader = p.id === view.players[state.trickLeaderIndex]?.id;
              const isNext = p.id === view.players[actorIdx]?.id;
              return (
                <div key={p.id} style={{ textAlign: 'center', minWidth: 60 }}>
                  <div style={{ fontSize: '0.75em', color: '#888', marginBottom: 2 }}>
                    {p.name}{isLeader && state.currentTrick.length === 0 ? ' ★' : ''}
                  </div>
                  {played ? (
                    <div style={{
                      fontSize: '1.4em', fontWeight: 'bold', padding: '4px 8px',
                      border: '1px solid #ccc', borderRadius: 4, background: '#fff',
                    }}>
                      <HeartsCardLabel card={played.card} />
                    </div>
                  ) : (
                    <div style={{
                      fontSize: '1.4em', padding: '4px 8px',
                      border: `1px dashed ${isNext ? '#f0a040' : '#ccc'}`,
                      borderRadius: 4, color: isNext ? '#f0a040' : '#ddd',
                    }}>
                      {isNext ? '?' : '—'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Passing instructions */}
      {canPass && (
        <div style={{ ...S.resultBox, marginBottom: 12, background: '#fffbe8', borderColor: '#d4b000' }}>
          <div style={{ marginBottom: 6 }}>Select 3 cards to pass <strong>{passDirectionLabel}</strong>:</div>
          <div style={{ color: '#888', fontSize: '0.85em' }}>
            {selectedPass.length}/3 selected
            {selectedPass.length > 0 && ': ' + selectedPass.map(c => cardName(c)).join(', ')}
          </div>
        </div>
      )}
      {state.phase === 'passing' && myPlayer?.passedCards !== null && !isSpectator && (
        <div style={{ ...S.resultBox, marginBottom: 12 }}>Waiting for others to pass…</div>
      )}

      {/* My hand */}
      {myPlayer && myPlayer.hand.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.8em', color: '#888', marginBottom: 6 }}>Your hand:</div>
          {([0, 1, 2, 3] as const).map(suit => {
            const suitCards = groupedHand[suit];
            if (suitCards.length === 0) return null;
            return (
              <div key={suit} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                {suitCards.map(card => {
                  const isSelected = selectedPass.includes(card);
                  const isValid = canPass ? true : isMyTurn ? validToPlay.includes(card) : false;
                  const isClickable = canPass || (isMyTurn && isValid);
                  const isQS = card === 11; // Q♠
                  return (
                    <button
                      key={card}
                      onClick={() => handleCardClick(card)}
                      disabled={!isClickable}
                      style={{
                        padding: '6px 10px',
                        border: `2px solid ${isSelected ? '#0077cc' : isQS ? '#d4a000' : isValid && (canPass || isMyTurn) ? '#4a9' : '#ccc'}`,
                        borderRadius: 6,
                        background: isSelected ? '#ddeeff' : isQS ? '#fffbe0' : '#fff',
                        cursor: isClickable ? 'pointer' : 'default',
                        opacity: (!canPass && isMyTurn && !isValid) ? 0.35 : 1,
                        fontWeight: isQS ? 700 : 'normal',
                        color: SUIT_COLORS[suit],
                        fontSize: '1em',
                      }}
                    >
                      {RANK_NAMES[cardRank(card)]}{SUIT_SYMBOLS[suit]}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Pass confirm button */}
      {canPass && selectedPass.length === 3 && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <button onClick={handlePass} style={{ ...S.btn, background: '#0077cc', color: '#fff', borderColor: '#0055aa' }}>
            Pass {selectedPass.map(c => cardName(c)).join(', ')} →
          </button>
        </div>
      )}

      {/* Waiting */}
      {state.phase === 'playing' && !isMyTurn && !state.winnerId && (
        <p style={{ color: '#888', textAlign: 'center' }}>
          Waiting for {view.players[actorIdx]?.name}…
        </p>
      )}

      {/* Round result */}
      {state.roundResult && state.phase === 'roundOver' && (
        <div style={{ ...S.resultBox, marginTop: 8 }}>
          {state.roundResult.shooterId ? (
            <div style={{ fontWeight: 'bold', color: '#c00', marginBottom: 4 }}>
              {state.players.find(p => p.id === state.roundResult!.shooterId)?.name} SHOT THE MOON! All opponents +26.
            </div>
          ) : null}
          <div style={{ fontSize: '0.9em', color: '#666' }}>
            {Object.entries(state.roundResult.pointsThisRound).map(([id, pts]) => {
              const name = state.players.find(p => p.id === id)?.name ?? id;
              return <span key={id} style={{ marginRight: 12 }}>{name}: +{pts}</span>;
            })}
          </div>
          <button onClick={() => send({ type: 'nextRound' })} style={{ ...S.btn, marginTop: 10 }}>Next Round</button>
        </div>
      )}

      {/* Game over */}
      {state.phase === 'complete' && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          {state.roundResult && (
            <div style={{ marginBottom: 8 }}>
              {state.roundResult.shooterId
                ? <span style={{ color: '#c00', fontWeight: 'bold' }}>
                    {state.players.find(p => p.id === state.roundResult!.shooterId)?.name} shot the moon!
                  </span>
                : null
              }
            </div>
          )}
          <div style={{ fontSize: '1.3em', fontWeight: 'bold', marginBottom: 8 }}>{winnerName} wins!</div>
          <div style={{ color: '#888', fontSize: '0.9em', marginBottom: 16 }}>
            {[...state.players].sort((a, b) => a.totalScore - b.totalScore).map(p =>
              <span key={p.id} style={{ marginRight: 12 }}>{p.name}: {p.totalScore} pts</span>
            )}
          </div>
          {isLeader ? <button onClick={onFinish} style={S.btn}>Back to the room</button> : <div style={{ color: '#888' }}>Waiting for the host to go back to the room.</div>}
        </div>
      )}
    </div>
  );
}

// ===== SPADES UI =====

function SpadesView({ state, myId, isSpectator, isLeader, send, onFinish }: {
  state: Spades.SpadesState; myId: string; isSpectator: boolean; isLeader: boolean;
  send: (a: Spades.SpadesAction) => void; onFinish: () => void;
}) {
  const myPlayer = state.players.find(p => p.id === myId) ?? null;
  const actorIdx = state.phase === 'playing'
    ? (state.trickLeaderIndex + state.currentTrick.length) % 4
    : -1;
  const isMyTurn = state.phase === 'playing' && state.players[actorIdx]?.id === myId && !isSpectator;
  const isMyBidTurn = state.phase === 'bidding' && state.players[state.biddingIndex]?.id === myId && !isSpectator;
  const validToPlay = isMyTurn ? Spades.validCardsForPlayer(state, myId) : [];
  const winnerTeam = state.winningTeam !== null ? state.teams[state.winningTeam] : null;
  const winnerNames = winnerTeam ? winnerTeam.playerIndices.map(i => state.players[i].name).join(' & ') : null;

  const groupedHand: Record<number, number[]> = { 0: [], 1: [], 2: [], 3: [] };
  if (myPlayer) {
    for (const card of myPlayer.hand) groupedHand[cardSuit(card)].push(card);
  }

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Round {state.roundNumber}</h2>
        <span style={{ color: '#888', fontSize: '0.85em' }}>
          {state.spadesBroken ? '♠ broken' : '♠ not broken'} · target: {state.config.targetScore}
        </span>
      </div>

      {/* Team scores */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {state.teams.map(team => {
          const players = team.playerIndices.map(i => state.players[i]);
          const isMyTeam = myPlayer?.teamIndex === team.teamIndex;
          return (
            <div key={team.teamIndex} style={{
              flex: 1, padding: '8px 12px', borderRadius: 6,
              background: isMyTeam ? '#e8f4ff' : '#f5f5f5',
              border: `1px solid ${isMyTeam ? '#90c4f0' : '#ddd'}`,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                Team {team.teamIndex === 0 ? 'A' : 'B'}{isMyTeam ? ' (yours)' : ''}
              </div>
              <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>{team.totalScore} pts</div>
              <div style={{ fontSize: '0.75em', color: '#888' }}>bags: {team.bags}</div>
              <div style={{ fontSize: '0.8em', marginTop: 4 }}>
                {players.map(p => <div key={p.id}>{p.name}{p.isBot ? ' 🤖' : ''}{p.id === myId ? ' (you)' : ''}</div>)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Player bid status */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {state.players.map((p, i) => {
          const isBidding = state.phase === 'bidding' && i === state.biddingIndex;
          const bidStr = p.bid !== null ? (p.isNilBid ? 'NIL' : `bid: ${p.bid}`) : state.phase === 'bidding' ? '...' : '?';
          return (
            <div key={p.id} style={{
              padding: '4px 10px', borderRadius: 4, fontSize: '0.8em',
              background: p.id === myId ? '#e8f4ff' : '#f5f5f5',
              border: `1px solid ${isBidding ? '#f0a040' : p.id === myId ? '#90c4f0' : '#ddd'}`,
            }}>
              <div>{p.name}{p.id === myId ? ' (you)' : ''}</div>
              <div style={{ color: '#666' }}>
                {bidStr}{state.phase === 'playing' && p.bid !== null ? ` · won: ${p.tricksWon}` : ''}
              </div>
            </div>
          );
        })}
      </div>

      {/* Current trick */}
      {(state.phase === 'playing' || state.currentTrick.length > 0) && (
        <div style={{ ...S.resultBox, marginBottom: 16 }}>
          <div style={{ fontSize: '0.8em', color: '#888', marginBottom: 6 }}>
            Trick {state.tricksPlayed + 1} of 13
          </div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            {state.players.map(p => {
              const played = state.currentTrick.find(tc => tc.playerId === p.id);
              const isTrickLeader = p.id === state.players[state.trickLeaderIndex]?.id;
              const isNext = p.id === state.players[actorIdx]?.id;
              return (
                <div key={p.id} style={{ textAlign: 'center', minWidth: 60 }}>
                  <div style={{ fontSize: '0.75em', color: '#888', marginBottom: 2 }}>
                    {p.name}{isTrickLeader && state.currentTrick.length === 0 ? ' ★' : ''}
                  </div>
                  {played ? (
                    <div style={{ fontSize: '1.4em', fontWeight: 'bold', padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, background: '#fff' }}>
                      <HeartsCardLabel card={played.card} />
                    </div>
                  ) : (
                    <div style={{ fontSize: '1.4em', padding: '4px 8px', border: `1px dashed ${isNext ? '#f0a040' : '#ccc'}`, borderRadius: 4, color: isNext ? '#f0a040' : '#ddd' }}>
                      {isNext ? '?' : '—'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bidding UI */}
      {isMyBidTurn && (
        <div style={{ ...S.resultBox, marginBottom: 16, background: '#fffbe8', borderColor: '#d4b000' }}>
          <div style={{ marginBottom: 8 }}>Your bid (0 = nil):</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
            {Array.from({ length: 14 }, (_, n) => (
              <button
                key={n}
                onClick={() => send({ type: 'bid', playerId: myId, amount: n })}
                style={{ ...S.btn, minWidth: 40, background: n === 0 ? '#ffe8a0' : '#f5f5f5', fontWeight: n === 0 ? 'bold' : 'normal' }}
              >
                {n === 0 ? 'NIL' : n}
              </button>
            ))}
          </div>
        </div>
      )}
      {state.phase === 'bidding' && !isMyBidTurn && !isSpectator && (
        <p style={{ color: '#888', textAlign: 'center' }}>
          Waiting for {state.players[state.biddingIndex]?.name} to bid…
        </p>
      )}

      {/* My hand (shown while bidding too: you bid on what you hold) */}
      {myPlayer && myPlayer.hand.length > 0 && (state.phase === 'playing' || state.phase === 'bidding') && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.8em', color: '#888', marginBottom: 6 }}>
            Your hand{myPlayer.bid !== null ? ` · bid ${myPlayer.isNilBid ? 'NIL' : myPlayer.bid}, won ${myPlayer.tricksWon}` : ''}:
          </div>
          {([0, 1, 2, 3] as const).map(suit => {
            const suitCards = groupedHand[suit];
            if (suitCards.length === 0) return null;
            return (
              <div key={suit} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                {suitCards.map(card => {
                  const isValid = isMyTurn && validToPlay.includes(card);
                  return (
                    <button
                      key={card}
                      onClick={() => isValid && send({ type: 'play', playerId: myId, card })}
                      disabled={!isValid}
                      style={{
                        padding: '6px 10px',
                        border: `2px solid ${isValid ? '#4a9' : '#ccc'}`,
                        borderRadius: 6,
                        background: '#fff',
                        cursor: isValid ? 'pointer' : 'default',
                        opacity: (isMyTurn && !isValid) ? 0.35 : 1,
                        color: SUIT_COLORS[suit],
                        fontSize: '1em',
                      }}
                    >
                      {RANK_NAMES[cardRank(card)]}{SUIT_SYMBOLS[suit]}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Waiting for turn */}
      {state.phase === 'playing' && !isMyTurn && !state.winnerId && (
        <p style={{ color: '#888', textAlign: 'center' }}>
          Waiting for {state.players[actorIdx]?.name}…
        </p>
      )}

      {/* Round result */}
      {state.roundResult && state.phase === 'roundOver' && (
        <div style={{ ...S.resultBox, marginTop: 8 }}>
          {state.roundResult.teams.map(tr => (
            <div key={tr.teamIndex} style={{ marginBottom: 8, textAlign: 'left' }}>
              <div style={{ fontWeight: 600 }}>
                Team {tr.teamIndex === 0 ? 'A' : 'B'}: bid {tr.nonNilBid}, won {tr.tricksWon} —{' '}
                <span style={{ color: tr.madeIt ? '#2a9' : '#c44' }}>
                  {tr.madeIt ? 'made it' : 'missed'} ({tr.roundPoints > 0 ? '+' : ''}{tr.roundPoints})
                </span>
              </div>
              {tr.bagPenalty > 0 && <div style={{ fontSize: '0.85em', color: '#c44' }}>bag penalty: −{tr.bagPenalty}</div>}
              {tr.nils.map(n => (
                <div key={n.playerId} style={{ fontSize: '0.85em', color: n.success ? '#2a9' : '#c44' }}>
                  {n.playerName} nil: {n.success ? 'success' : 'failed'} ({n.points > 0 ? '+' : ''}{n.points})
                </div>
              ))}
            </div>
          ))}
          <button onClick={() => send({ type: 'nextRound' })} style={{ ...S.btn, marginTop: 10 }}>Next Round</button>
        </div>
      )}

      {/* Game over */}
      {state.phase === 'complete' && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <div style={{ fontSize: '1.3em', fontWeight: 'bold', marginBottom: 8 }}>
            Team {state.winningTeam === 0 ? 'A' : 'B'} wins!
          </div>
          {winnerNames && <div style={{ color: '#666', marginBottom: 8 }}>{winnerNames}</div>}
          <div style={{ color: '#888', fontSize: '0.9em', marginBottom: 16 }}>
            {state.teams.map(t => (
              <span key={t.teamIndex} style={{ marginRight: 16 }}>
                Team {t.teamIndex === 0 ? 'A' : 'B'}: {t.totalScore} pts
              </span>
            ))}
          </div>
          {isLeader ? <button onClick={onFinish} style={S.btn}>Back to the room</button> : <div style={{ color: '#888' }}>Waiting for the host to go back to the room.</div>}
        </div>
      )}
    </div>
  );
}

// ===== Shared styles =====

const S = {
  container: {
    maxWidth: 560,
    margin: '32px auto',
    padding: '0 16px',
    fontFamily: 'sans-serif',
  } as React.CSSProperties,
  playerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 6,
  } as React.CSSProperties,
  resultBox: {
    padding: '10px 14px',
    background: '#fffbea',
    border: '1px solid #f0d080',
    borderRadius: 6,
    textAlign: 'center',
    fontSize: '0.95em',
  } as React.CSSProperties,
  btn: {
    padding: '8px 20px',
    fontSize: '0.95em',
    cursor: 'pointer',
    borderRadius: 4,
    border: '1px solid #ccc',
    background: '#f5f5f5',
  } as React.CSSProperties,
  smallBtn: {
    padding: '3px 6px',
    fontSize: '0.7em',
    cursor: 'pointer',
    borderRadius: 3,
    border: '1px solid #ccc',
    background: '#f5f5f5',
    width: '100%',
  } as React.CSSProperties,
};
